import _ from "lodash";
import { useEffect, useState } from "react";
import 'chart.js/auto';
import { Chart } from 'react-chartjs-2';
import CustomDotIcon from "../../../../../components/icon/dot-icon/v1/CustomDotIcon";
import { GraphDataset, createGraphData, createGraphOption, setAnalysisResultText } from "../../../../../utils/chartUtils";
import { getRandomColors } from "../../../../../utils/colorUtils";
import { dateToYYYYMMDDAndDayName, getDateFormatByGraphDateLabel, getEndDate, getMonthAndSearchDateRange, getStartDate, getWeekNumberAndSearchDateRange } from "../../../../../utils/dateFormatUtils";
import { toPriceUnitFormat } from "../../../../../utils/numberFormatUtils";
import useRouterHookV2 from "../../../../../hooks/router/useRouterHookV2";
import { CustomChartGroupV2 } from "../../../../../components/chart/v2/CustomChartGroup";
import { customToast, defaultOptions } from "../../../../../components/toast/custom-react-toastify/v1";
import FieldCircleLoading from "../../../../../components/loading/field-loading/v1/FieldCircleLoading";

const SALES_GRAPH_BG_COLOR = ['#6EB9C5', '#61CBC6', '#6CDCBA', '#5FA6C7', '#8EEAA3', '#698FC1', '#BFF388', '#F9F871'];
export default function PayAmountGraphComponent(props) {
    const [salesGraphData, setSalesGraphData] = useState(null);
    const [totalGraphData, setTotalGraphData] = useState(null);
    
    const [salesSummaryData, setSalesSummaryData] = useState(null);
    const [totalSummaryData, setTotalSummaryData] = useState(null);
    
    const [graphOption, setGraphOption] = useState(null);
    const [graphLabels, setGraphLabels] = useState(null);

    const router = useRouterHookV2();

    useEffect(() => {
        if(!router.state?.productSubCategoryIds) {
            handleActionResetGraphData();
            return;
        }

        if(!(props.subCategories?.length > 0)) {
            return;
        }

        if(!props.searchDimension) {
            return;
        }

        if(!props.analysis) {
            return;
        }

        handleActionCreateGraphData();
    }, [router.state, props.analysis, props.subCategories, props.searchDimension, props.checkedExcludedDeliveryChargeSwitch])

    useEffect(() => {
        if(!(salesGraphData && totalGraphData)) {
            return;
        }

        handleActionCreateGraphOption();
    }, [salesGraphData, totalGraphData])

    const handleActionResetGraphData = () => {
        setSalesGraphData(null);
        setTotalGraphData(null);
        setSalesSummaryData(null);
        setTotalSummaryData(null);
    }

    const handleActionCreateGraphData = () => {
        let salesPayAmountData = [];
        let orderPayAmountData = [];

        let salesDatasets = [];
        let orderDatasets = [];
        let salesAvgDatasets = [];
        let graphLabels = new Set([]);
        let subCategoryIds = [...router.state.productSubCategoryIds];
        let selectedSubCategories = props.subCategories.filter(r => subCategoryIds.includes(r.id));

        // 날짜 최소값, 최대값 설정
        let minimumDate = props.analysis[0].datetime;
        let maximumDate = props.analysis.slice(-1)[0].datetime;

        selectedSubCategories.forEach(subCategory => {
            let salesPayAmount = [];
            let orderPayAmount = [];
            let analysisMap = new Map();

            props.analysis.forEach(r => {
                let datetime = dateToYYYYMMDDAndDayName(r.datetime);
                if (props.searchDimension === 'week') {
                    datetime = getWeekNumberAndSearchDateRange(r.datetime, minimumDate, maximumDate);
                } else if (props.searchDimension === 'month') {
                    datetime = getMonthAndSearchDateRange(r.datetime, minimumDate, maximumDate);
                }
    
                let performance = r.performances?.filter(r2 => r2.productSubCategoryId === subCategory.id)[0];
                let data = analysisMap.get(datetime);
                let viewSalesPayAmount = props.checkedExcludedDeliveryChargeSwitch ? performance?.excludedDeliveryChargeSalesPayAmount : performance?.salesPayAmount;
                let viewOrderPayAmount = props.checkedExcludedDeliveryChargeSwitch ? performance?.excludedDeliveryChargeOrderPayAmount : performance?.orderPayAmount;

                analysisMap.set(datetime, {
                    salesPayAmount: (data?.salesPayAmount || 0) + (viewSalesPayAmount || 0),
                    orderPayAmount: (data?.orderPayAmount || 0) + (viewOrderPayAmount || 0)
                });
            })
            
            analysisMap.forEach((value, key) => {
                graphLabels.add(key);
                salesPayAmount.push(value.salesPayAmount);
                orderPayAmount.push(value.orderPayAmount);
            })

            salesPayAmountData.push(salesPayAmount);
            orderPayAmountData.push(orderPayAmount);
        })

        let randomColor = getRandomColors(selectedSubCategories.length - SALES_GRAPH_BG_COLOR.length)
        let graphColor = [...SALES_GRAPH_BG_COLOR, ...randomColor];

        // 그래프 데이터 세팅
        if (selectedSubCategories.size === 0) {
            let barGraphOfOrder = {
                ...new GraphDataset().toJSON(),
                label: '주문 매출액',
                data: [],
                type: 'bar',
                fill: false,
                borderColor: graphColor[0],
                backgroundColor: graphColor[0],
                order: -1,
                pointRadius: 2
            }
            let barGraphOfSales = {
                ...new GraphDataset().toJSON(),
                type: 'bar',
                label: '판매 매출액',
                data: [],
                borderColor: graphColor[0],
                backgroundColor: graphColor[0],
                borderWidth: 0,
                order: 0
            }

            orderDatasets.push(barGraphOfOrder);
            salesDatasets.push(barGraphOfSales);
        } else {
            selectedSubCategories.forEach((r, idx) => {
                let barGraphOfOrder = {
                    ...new GraphDataset().toJSON(),
                    type: 'bar',
                    label: '(주문) ' + r.name,
                    data: orderPayAmountData[idx],
                    stack: 'group' + idx,
                    borderColor: graphColor[idx] + '55',
                    backgroundColor: graphColor[idx] + '55',
                    borderWidth: 0,
                    order: 1
                }
                let barGraphOfSales = {
                    ...new GraphDataset().toJSON(),
                    type: 'bar',
                    label: r.name,
                    stack: 'group' + idx,
                    data: salesPayAmountData[idx],
                    borderColor: graphColor[idx],
                    backgroundColor: graphColor[idx],
                    borderWidth: 0,
                    order: 0
                }

                orderDatasets.push(barGraphOfOrder);
                salesDatasets.push(barGraphOfSales);

                // 판매매출액 7일간 평균 데이터 생성
                // 조회된 기간의 시작날짜부터 +6일은 null로 채운다
                let salesPayAmountAvgData = Array(6).fill(null, 0, 6);
                for (let i = 6; i <= salesPayAmountData[idx].length; i++) {
                    let avg = parseInt(salesPayAmountData[idx].slice(i - 6, i + 1).reduce((a, b) => a + b) / 7);
                    salesPayAmountAvgData.push(avg);
                }

                // 판매매출액 7일간 평균 그래프 데이터 생성
                let lineGraphOfSalesAvg = {
                    ...new GraphDataset().toJSON(),
                    type: 'line',
                    label: r.name + ' 7일간 평균',
                    data: salesPayAmountAvgData,
                    fill: false,
                    borderColor: graphColor[idx],
                    backgroundColor: graphColor[idx],
                    order: -2,
                    borderDash: [3, 3]
                }

                salesAvgDatasets.push(lineGraphOfSalesAvg);
            })
        }

        // 매출 그래프 데이터 생성
        setSalesGraphData({
            labels: [...graphLabels],
            datasets: [...salesDatasets, ...salesAvgDatasets]
        });
        setTotalGraphData({
            labels: [...graphLabels],
            datasets: [...salesDatasets, ...orderDatasets, ...salesAvgDatasets]
        });
        setGraphLabels([...graphLabels]);

        // 매출 그래프 요약 데이터 생성
        handleActionCreateSalesSummary(salesDatasets);
        handleActionCreateTotalSummary([...salesDatasets, ...orderDatasets])
    }

    const handleActionCreateSalesSummary = (data) => {
        let salesData = setAnalysisResultText(data);

        // 매출액 내림차순으로 정렬
        salesData = _.orderBy(salesData, 'value', 'desc');
        setSalesSummaryData(salesData);
    }

    const handleActionCreateTotalSummary = (data) => {
        let totalData = setAnalysisResultText(data);

        totalData = _.orderBy(totalData, 'value', 'desc');
        setTotalSummaryData(totalData);
    }

    const handleActionCreateGraphOption = () => {
        let option = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            scales: {
                y: {
                    ticks: {
                        callback: function (value, index, ticks) {
                            return toPriceUnitFormat(value);
                        }
                    },
                    stacked: false
                },
                x: {
                    grid: {
                        display: false
                    }
                }
            },
            plugins: {
                legend: {
                    labels: {
                        fullSize: true
                    }
                }
            },
            onClick: function (e, item) {
                handleActionSetGraphClickOption(item);
            },
            onHover: (e, item) => {
                const target = e.native ? e.native.target : e.target;
                target.style.cursor = item[0] ? 'pointer' : 'default';
            }
        }

        setGraphOption(option);
    }
    const handleActionSetGraphClickOption = (item) => {
        if (item.length === 0) return;

        var itemIdx = item[0].index;
        var label = graphLabels[itemIdx];
        var date = getDateFormatByGraphDateLabel(label, props.searchDimension);

        let startDate = getStartDate(date.startDate);
        let endDate = getEndDate(date.endDate);

        let searchValue = {
            state: {
                ...router.state,
                startDate,
                endDate
            }
        }
        router.replace(searchValue);
        props.onActionOpenDetailGraphSelectorModal();
    }

    const handleActionOpenWholePeroidDetailGraphSelectorModal = () => {
        if(!(router.state?.productSubCategoryIds?.length > 0)) {
            let memo = '카테고리를 먼저 선택해주세요.';
            customToast.warning(memo, {
                ...defaultOptions,
                toastId: memo
            })
            return;
        }
        
        let startDate = getStartDate(props.analysis[0].datetime);
        let endDate = getEndDate(props.analysis.slice(-1)[0].datetime);

        let searchValue = {
            state: {
                ...router.state,
                startDate,
                endDate
            }
        }
        router.replace(searchValue);
        props.onActionOpenDetailGraphSelectorModal();
    }
    
    return (
        <CustomChartGroupV2>
            <CustomChartGroupV2.BoardGroup className='flex-item'>
                <div>
                    <CustomChartGroupV2.BoardGroup.Title>총 매출액</CustomChartGroupV2.BoardGroup.Title>
                    <CustomChartGroupV2.BoardGroup.Info>선택된 서브 카테고리의 총 판매액이 표시되며, 일별 조회 시 7일간 평균 차트를 확인할 수 있습니다.</CustomChartGroupV2.BoardGroup.Info>
                </div>
                <CustomChartGroupV2.BoardGroup.SearchModalButton onActionModalOpen={handleActionOpenWholePeroidDetailGraphSelectorModal} />
            </CustomChartGroupV2.BoardGroup>

            <div style={{ position: 'relative' }}>
                {props.isSubCategoryAnalysisLoading &&
                    <FieldCircleLoading boxStyle={{ borderRadius: '15px' }} />
                }
                <CustomChartGroupV2.BodyGroup className='flex-item'>
                    <CustomChartGroupV2.BodyGroup.ChartBody>
                        {totalGraphData && graphOption &&
                            <Chart
                                data={createGraphData(props.checkedSwitch ? totalGraphData : salesGraphData)}
                                options={createGraphOption(graphOption)}
                            />
                        }
                    </CustomChartGroupV2.BodyGroup.ChartBody>

                    <CustomChartGroupV2.BodyGroup.SummaryGroup>
                        <CustomChartGroupV2.BodyGroup.SummaryGroup.SummaryTitle>[서브 카테고리 총 매출액]</CustomChartGroupV2.BodyGroup.SummaryGroup.SummaryTitle>
                        <CustomChartGroupV2.BodyGroup.SummaryGroup.SummaryContent>
                            <SummaryContentTextField
                                data={props.checkedSwitch ? totalSummaryData : salesSummaryData}
                            />
                        </CustomChartGroupV2.BodyGroup.SummaryGroup.SummaryContent>
                    </CustomChartGroupV2.BodyGroup.SummaryGroup>
                </CustomChartGroupV2.BodyGroup>
            </div>
        </CustomChartGroupV2>
    )
}

function SummaryContentTextField({ data }) {
    return (
        <ul>
            {data?.map((r, idx) => {
                return (
                    <li key={'graph-summary' + idx}>
                        <div style={{
                            width: '120px',
                            overflow:'hidden',
                            textOverflow:'ellipsis',
                            whiteSpace:'nowrap'
                         }}>
                            <CustomDotIcon color={r.color} />
                            <span> {r.label} </span>
                        </div>
                        <div style={{ fontWeight: 700 }}>
                            {toPriceUnitFormat(r.value)}
                        </div>
                    </li>
                )
            })}
            {!(data && data.length > 0) &&
                <li>
                    <span>데이터가 존재하지 않습니다.</span>
                </li>
            }
        </ul>
    )
}
